import { useState, useEffect } from "react";
import {
  Table,
  Badge,
  Button,
  Form,
  Dropdown,
  OverlayTrigger,
  Overlay,
  Tooltip,
  Popover,
} from "react-bootstrap";
import { FaPen, FaTrash } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomPagination from "../../../components/CustomPagination";
import { deleteFollowUp } from "../../../api/lead";

const FollowupTable = ({ data = [] }) => {
  const userType = localStorage.getItem("type");
  const navigate = useNavigate();

  /* ---------------- URL STATE ---------------- */
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get("page")) || 1;
  const leadsPerPage = Number(searchParams.get("limit")) || 10;
  const dateSortOrder = searchParams.get("sort") || "desc";

  const updateParams = (params) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(params).forEach(([key, value]) => {
        next.set(key, value);
      });
      return next;
    });
  };

  /* ---------------- STATS STATE ---------------- */
  const [todayFollowups, setTodayFollowups] = useState(0);
  const [yesterdayFollowups, setYesterdayFollowups] = useState(0);
  const [monthFollowups, setMonthFollowups] = useState(0);
  const [todayChangePercent, setTodayChangePercent] = useState(0);

  /* ---------------- DELETE STATE ---------------- */
  const [showPopover, setShowPopover] = useState(false);
  const [target, setTarget] = useState(null);
  const [activeLeadId, setActiveLeadId] = useState(null);

  /* ---------------- STATS EFFECT ---------------- */
  useEffect(() => {
    if (!Array.isArray(data)) return;

    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split("T")[0];

    let todayCount = 0;
    let yesterdayCount = 0;
    let monthCount = 0;

    data.forEach((lead) => {
      if (!lead?.time) return;
      const date = new Date(lead.time);
      if (isNaN(date)) return;

      const dateStr = date.toISOString().split("T")[0];
      if (dateStr === todayStr) todayCount++;
      if (dateStr === yesterdayStr) yesterdayCount++;
      if (
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      ) {
        monthCount++;
      }
    });

    setTodayFollowups(todayCount);
    setYesterdayFollowups(yesterdayCount);
    setMonthFollowups(monthCount);

    const change =
      yesterdayCount === 0
        ? 100
        : (((todayCount - yesterdayCount) / yesterdayCount) * 100).toFixed(1);

    setTodayChangePercent(change);
  }, [data]);

  /* ---------------- SORTING ---------------- */
  const sortedData = [...data].sort((a, b) => {
    const dateA = a?.followup_time?.$date
      ? new Date(a.followup_time.$date)
      : new Date(0);
    const dateB = b?.followup_time?.$date
      ? new Date(b.followup_time.$date)
      : new Date(0);
    return dateSortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });

  /* ---------------- PAGINATION ---------------- */
  const totalLeads = sortedData.length;
  const totalPages = Math.ceil(totalLeads / leadsPerPage);

  const start = totalLeads === 0 ? 0 : (currentPage - 1) * leadsPerPage + 1;
  const end = Math.min(currentPage * leadsPerPage, totalLeads);

  const paginatedData = sortedData.slice(start - 1, end);

  /* ---------------- HANDLERS ---------------- */
  const handleEdit = (lead_id, lead_data) => {
    navigate(`/home/lead-edit/L-${`*******${lead_id.slice(-10)}`}`, {
      state: { lead_data: lead_data[0], source: "followups" },
    });
  };

  const handleDeleteClick = (event, lead_id) => {
    setShowPopover(true);
    setTarget(event.target);
    setActiveLeadId(lead_id);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteFollowUp({
        lead_id: activeLeadId,
        action: "delete",
      });
      if (response.status) {
        alert("Followup Deleted successfully!");
        window.location.reload();
      }
    } catch {
      alert("Error deleting followup");
    } finally {
      setShowPopover(false);
      setActiveLeadId(null);
    }
  };

  /* ---------------- RENDER ---------------- */
  return (
    <>
      {/* STATS */}
      <div className="row g-3 mb-4">
        {[
          ["Today", todayFollowups, todayChangePercent],
          ["Yesterday", yesterdayFollowups],
          ["This Month", monthFollowups],
        ].map(([label, value, change], idx) => (
          <div className="col" key={idx}>
            <div className="card p-3">
              <h6 className="mb-0">
                Followups {label}
                {change !== undefined && (
                  <span style={{ fontSize: "0.7rem" }}>
                    {" "}
                    ({change}% Change)
                  </span>
                )}
              </h6>
              <p className="mb-0 fs-5 fw-bold">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* TABLE */}
      <Table striped bordered hover responsive className="bg-white">
        <thead className="bg-light">
          <tr>
            <th>Lead ID</th>
            <th>Name</th>
            {(userType === "Admin" || userType === "Manager") && (
              <th>Created By</th>
            )}
            <th>Type</th>
            <th>Status</th>
            <th>
              <Dropdown>
                <Dropdown.Toggle variant="light" className="w-100 text-start">
                  Followup Time
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    active={dateSortOrder === "desc"}
                    onClick={() => updateParams({ sort: "desc", page: 1 })}
                  >
                    Newest to Oldest
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={dateSortOrder === "asc"}
                    onClick={() => updateParams({ sort: "asc", page: 1 })}
                  >
                    Oldest to Newest
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {paginatedData.length ? (
            paginatedData.map((f, i) => (
              <tr key={i}>
                <td>L-{`*******${f.lead_id.slice(-10)}`}</td>
                <td>{f?.lead_data?.[0]?.name}</td>
                {(userType === "Admin" || userType === "Manager") && (
                  <td>{f?.assigned_data?.[0]?.name}</td>
                )}
                <td>{f?.followup_name}</td>
                <td>
                  <Badge className={f?.followup_status?.[0]?.bg_color}>
                    {f?.followup_status?.[0]?.name}
                  </Badge>
                </td>
                <td>
                  {f?.followup_time?.$date
                    ? new Date(f.followup_time.$date).toLocaleString()
                    : ""}
                </td>
                <td>
                  <FaPen
                    className="me-2"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleEdit(f.lead_id, f.lead_data)}
                  />
                  <FaTrash
                    color="red"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleDeleteClick(e, f.lead_id)}
                  />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9" className="text-center text-muted">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* PAGINATION */}
      <div className="d-flex justify-content-between align-items-center my-3">
        <span className="text-muted">
          Showing {start}–{end} of {totalLeads}
        </span>

        <Form.Select
          value={leadsPerPage}
          onChange={(e) =>
            updateParams({ limit: e.target.value, page: 1 })
          }
          style={{ width: "80px" }}
        >
          {[10, 20, 30, 40, 50].map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </Form.Select>

        <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => updateParams({ page: p })}
        />
      </div>
    </>
  );
};

export default FollowupTable;
